import React, { FC, useContext } from 'react';
import {
  Span,
  Label,
  H2,
  H3,
  ListGroupRow,
  ListGroup,
} from '../../../../shared/typefaces/Typefaces.styles';
import { Container, CostLink, Header, ListColumns } from './Cost.styles';
import {
  capitalizeFirstLetter,
  convertCostCleanly,
  convertPercentageCleanly,
  isEmpty,
  blurContent,
  convertNumberCleanly,
} from '../../../../../utils/helper';
import { LayoutContext } from '../../../../../contexts/layoutContext';
import { ICostProps } from './cost.types';
import { useRestrictionPopup } from '../../../../../utils/customHooks/useRestrictionPopup';
import { restrictionPopupMessages } from '../../../../../utils/restrictionPopupMessages';
import {
  checkIfAccountIsNotComplete,
  useRestrictionCheck,
  userHasCalculatorAddon,
} from '../../../../../utils/helper/account';
import { AccountType } from '../../../../../generated/graphql';
import { overlayConstants } from '../../../../shared/layout/layoutOverlay/constants';

const Cost: FC<ICostProps> = ({
  recipeData: {
    recipeCost,
    costPerServe,
    foodCost,
    foodCostPercentage,
    cogs,
    cogsPercentage,
    totalStaffTimeCost,
    totalVenueTimeCost,
    rentCost,
    powerCost,
    waterCost,
    insuranceCost,
    councilCost,
    wastageCost,
    wastageType,
    weeklyRecipeRemakes,
    recipeRevenue,
    serves,
  },
  venue,
}) => {
  const { selectedRecipeVersion, account, user, dispatch } = useContext(
    LayoutContext
  );
  const showRestrictionPopup = useRestrictionPopup();
  const checkRecipeProfitRestrictions = useRestrictionCheck([
    'CALCULATE_RECIPE_PROFIT',
  ]);

  const handleRecipeRevenueUpgrade = () => {
    showRestrictionPopup(
      restrictionPopupMessages.cannotDisplayRecipeVersionProfit
    );
  };

  const handleEditRecipe = (recipeVersionId: string | undefined) => {
    dispatch({ type: 'SLIDER_PAGE', payload: 'editRecipe' });
    dispatch({ type: 'SELECT_RECIPE_VERSION', payload: recipeVersionId });
    dispatch({ type: 'DUPLICATE_RECIPE', payload: false });
    dispatch({ type: 'SCALE_RECIPE', payload: false });
    if (checkIfAccountIsNotComplete(user?.email, account?.type)) {
      dispatch({
        type: 'SET_OVERLAY',
        payload: overlayConstants.noAccountEditRecipe,
      });
    }
  };

  const updateVenueCosts = () => {
    if (checkIfAccountIsNotComplete(user?.email, account?.type)) {
      dispatch({
        type: 'SET_OVERLAY',
        payload: overlayConstants.noAccountUpdateVenueCosts,
      });
    } else {
      dispatch({ type: 'SLIDER_PAGE', payload: 'venue' });
    }
  };

  const venueData = venue?.data?.venue?.userVenue?.venue;
  const { validUserCalculatorAddOn } = userHasCalculatorAddon(account?.addOns);
  const restrictionCheck = checkRecipeProfitRestrictions({
    data:
      validUserCalculatorAddOn ||
      account?.type === AccountType.Feedback ||
      account?.type === AccountType.RecipeRevenue,
  });
  const showProfitContent = validUserCalculatorAddOn || restrictionCheck.isPass;

  return (
    <Container>
      <Header>
        <div>
          <H2 fontSize="large">Cost Breakdown</H2>
          <Span fontSize="small" color="faded">
            per recipe batch
          </Span>
        </div>
        <div className="right">
          <H2 fontSize="large" className="cogsHeading">
            <Span
              fontSize="small"
              fontWeight="semibold"
              color="grey"
              className="cogsSubHeading"
            >
              COGS
            </Span>
            <Span fontSize="large" fontWeight="semibold" color="black">
              {convertCostCleanly(cogs)}
            </Span>
          </H2>
          <Span fontSize="default" color="faded" className="cogsPercentage">
            <Span fontSize="small" color="grey" className="cogsSubHeading">
              percentage
            </Span>
            <Span fontSize="small" fontWeight="semibold" color="grey">
              {convertPercentageCleanly(cogsPercentage)}
            </Span>
          </Span>
        </div>
      </Header>
      <ListColumns colOneWidth="1fr" colTwoWidth="1fr" padding="small">
        <div>
          <H3 fontWeight="semibold" color="black">
            Overview
          </H3>
          <ListGroup>
            <ListGroupRow colOneWidth="180px" colTwoWidth="auto">
              <Label color="grey">Total Ingredient Cost</Label>
              {!isEmpty(foodCost) ? (
                <Span
                  className={`${!showProfitContent && 'blur hasSeeIcon'}`}
                  onClick={() =>
                    !showProfitContent && handleRecipeRevenueUpgrade()
                  }
                >
                  {showProfitContent
                    ? convertCostCleanly(foodCost)
                    : blurContent(convertCostCleanly(foodCost))}
                </Span>
              ) : (
                <CostLink
                  color="primary"
                  fontSize="small"
                  onClick={() => handleEditRecipe(selectedRecipeVersion)}
                >
                  Add Ingredients
                </CostLink>
              )}
            </ListGroupRow>
            <ListGroupRow colOneWidth="180px" colTwoWidth="auto">
              <Label color="grey">Food Cost Per Serve</Label>
              {!isEmpty(totalStaffTimeCost) ? (
                <Span
                  className={`${!showProfitContent && 'blur hasSeeIcon'}`}
                  onClick={() =>
                    !showProfitContent && handleRecipeRevenueUpgrade()
                  }
                >
                  {showProfitContent
                    ? convertCostCleanly(foodCost / serves)
                    : blurContent(convertCostCleanly(foodCost / serves))}
                </Span>
              ) : (
                <CostLink
                  color="primary"
                  fontSize="small"
                  onClick={() => handleEditRecipe(selectedRecipeVersion)}
                >
                  Add Ingredients
                </CostLink>
              )}
            </ListGroupRow>
            <ListGroupRow colOneWidth="180px" colTwoWidth="auto">
              <Label color="grey">COGS per serve</Label>
              {!isEmpty(cogs) ? (
                <Span
                  className={`${!showProfitContent && 'blur hasSeeIcon'}`}
                  onClick={() =>
                    !showProfitContent && handleRecipeRevenueUpgrade()
                  }
                >
                  {showProfitContent
                    ? convertCostCleanly(cogs / serves)
                    : blurContent(convertCostCleanly(cogs / serves))}
                </Span>
              ) : (
                <CostLink
                  color="primary"
                  fontSize="small"
                  onClick={() => handleEditRecipe(selectedRecipeVersion)}
                >
                  Add Ingredients
                </CostLink>
              )}
            </ListGroupRow>
          </ListGroup>
          <ListGroup>
            <ListGroupRow colOneWidth="180px" colTwoWidth="auto">
              <Label color="grey">Food Cost Percentage</Label>
              {!isEmpty(recipeCost) ? (
                <Span
                  className={`${!showProfitContent && 'blur hasSeeIcon'}`}
                  onClick={() =>
                    !showProfitContent && handleRecipeRevenueUpgrade()
                  }
                >
                  {showProfitContent
                    ? convertPercentageCleanly(foodCostPercentage)
                    : blurContent(convertPercentageCleanly(foodCostPercentage))}
                </Span>
              ) : (
                <CostLink
                  color="primary"
                  fontSize="small"
                  onClick={() => handleEditRecipe(selectedRecipeVersion)}
                >
                  Add Ingredients
                </CostLink>
              )}
            </ListGroupRow>
            <ListGroupRow colOneWidth="180px" colTwoWidth="auto">
              <Label color="grey">Total Recipe Cost</Label>
              <Span
                className={`${!showProfitContent && 'blur hasSeeIcon'}`}
                onClick={() =>
                  !showProfitContent && handleRecipeRevenueUpgrade()
                }
              >
                {showProfitContent
                  ? convertCostCleanly(recipeCost)
                  : blurContent(convertCostCleanly(recipeCost))}
              </Span>
            </ListGroupRow>
            <ListGroupRow colOneWidth="180px" colTwoWidth="auto">
              <Label color="grey">Total Cost Per Serve</Label>
              <Span
                className={`${!showProfitContent && 'blur hasSeeIcon'}`}
                onClick={() =>
                  !showProfitContent && handleRecipeRevenueUpgrade()
                }
              >
                {showProfitContent
                  ? convertCostCleanly(costPerServe)
                  : blurContent(convertCostCleanly(costPerServe))}
              </Span>
            </ListGroupRow>
          </ListGroup>
          <ListGroup>
            <ListGroupRow colOneWidth="180px" colTwoWidth="auto">
              <Label color="grey">Total Ingredient Cost</Label>
              {!isEmpty(foodCost) ? (
                <Span
                  className={`${!showProfitContent && 'blur hasSeeIcon'}`}
                  onClick={() =>
                    !showProfitContent && handleRecipeRevenueUpgrade()
                  }
                >
                  {showProfitContent
                    ? convertCostCleanly(foodCost)
                    : blurContent(convertCostCleanly(foodCost))}
                </Span>
              ) : (
                <CostLink
                  color="primary"
                  fontSize="small"
                  onClick={() => handleEditRecipe(selectedRecipeVersion)}
                >
                  Add Ingredients
                </CostLink>
              )}
            </ListGroupRow>
            <ListGroupRow colOneWidth="180px" colTwoWidth="auto">
              <Label color="grey">Staff Cost</Label>
              {!isEmpty(totalStaffTimeCost) ? (
                <Span
                  className={`${!showProfitContent && 'blur hasSeeIcon'}`}
                  onClick={() =>
                    !showProfitContent && handleRecipeRevenueUpgrade()
                  }
                >
                  {showProfitContent
                    ? convertCostCleanly(totalStaffTimeCost)
                    : blurContent(convertCostCleanly(totalStaffTimeCost))}
                </Span>
              ) : (
                <CostLink
                  color="primary"
                  fontSize="small"
                  onClick={() => handleEditRecipe(selectedRecipeVersion)}
                >
                  Add Staff Tasks
                </CostLink>
              )}
            </ListGroupRow>
            <ListGroupRow colOneWidth="180px" colTwoWidth="auto">
              <Label color="grey">Venue Cost</Label>
              {!isEmpty(totalVenueTimeCost) ? (
                <Span
                  className={`${!showProfitContent && 'blur hasSeeIcon'}`}
                  onClick={() =>
                    !showProfitContent && handleRecipeRevenueUpgrade()
                  }
                >
                  {showProfitContent
                    ? convertCostCleanly(totalVenueTimeCost)
                    : blurContent(convertCostCleanly(totalVenueTimeCost))}
                </Span>
              ) : (
                <CostLink
                  color="primary"
                  fontSize="small"
                  onClick={() => updateVenueCosts()}
                >
                  Update venue costs
                </CostLink>
              )}
            </ListGroupRow>
            <ListGroupRow colOneWidth="180px" colTwoWidth="auto">
              <Label color="grey">Weekly Remakes</Label>
              <Span>
                {convertNumberCleanly(Number(weeklyRecipeRemakes?.toFixed()))}
              </Span>
            </ListGroupRow>
          </ListGroup>
        </div>
        <div>
          <H3 fontWeight="semibold" color="black">
            Venue Costs
          </H3>
          <ListGroup>
            <ListGroupRow colOneWidth="180px" colTwoWidth="auto">
              <Label color="grey">Rent</Label>
              {!isEmpty(rentCost) ? (
                <Span
                  className={`${!showProfitContent && 'blur hasSeeIcon'}`}
                  onClick={() =>
                    !showProfitContent && handleRecipeRevenueUpgrade()
                  }
                >
                  {showProfitContent
                    ? convertCostCleanly(rentCost)
                    : blurContent(convertCostCleanly(rentCost))}
                </Span>
              ) : (
                <CostLink
                  color="primary"
                  fontSize="small"
                  onClick={() => updateVenueCosts()}
                >
                  Update rent cost
                </CostLink>
              )}
            </ListGroupRow>
            <ListGroupRow colOneWidth="180px" colTwoWidth="auto">
              <Label color="grey">Power</Label>
              {!isEmpty(powerCost) ? (
                <Span
                  className={`${!showProfitContent && 'blur hasSeeIcon'}`}
                  onClick={() =>
                    !showProfitContent && handleRecipeRevenueUpgrade()
                  }
                >
                  {showProfitContent
                    ? convertCostCleanly(powerCost)
                    : blurContent(convertCostCleanly(powerCost))}
                </Span>
              ) : (
                <CostLink
                  color="primary"
                  fontSize="small"
                  onClick={() => updateVenueCosts()}
                >
                  Update power cost
                </CostLink>
              )}
            </ListGroupRow>
            <ListGroupRow colOneWidth="180px" colTwoWidth="auto">
              <Label color="grey">Water</Label>
              {!isEmpty(waterCost) ? (
                <Span
                  className={`${!showProfitContent && 'blur hasSeeIcon'}`}
                  onClick={() =>
                    !showProfitContent && handleRecipeRevenueUpgrade()
                  }
                >
                  {showProfitContent
                    ? convertCostCleanly(waterCost)
                    : blurContent(convertCostCleanly(waterCost))}
                </Span>
              ) : (
                <CostLink
                  color="primary"
                  fontSize="small"
                  onClick={() => updateVenueCosts()}
                >
                  Update water cost
                </CostLink>
              )}
            </ListGroupRow>
          </ListGroup>
          <ListGroup noBorder>
            <ListGroupRow colOneWidth="180px" colTwoWidth="auto">
              <Label color="grey">Insurance</Label>
              {!isEmpty(insuranceCost) ? (
                <Span
                  className={`${!showProfitContent && 'blur hasSeeIcon'}`}
                  onClick={() =>
                    !showProfitContent && handleRecipeRevenueUpgrade()
                  }
                >
                  {showProfitContent
                    ? convertCostCleanly(insuranceCost)
                    : blurContent(convertCostCleanly(insuranceCost))}
                </Span>
              ) : (
                <CostLink
                  color="primary"
                  fontSize="small"
                  onClick={() => updateVenueCosts()}
                >
                  Update insurance cost
                </CostLink>
              )}
            </ListGroupRow>
            <ListGroupRow colOneWidth="180px" colTwoWidth="auto">
              <Label color="grey">Council</Label>
              {!isEmpty(councilCost) ? (
                <Span
                  className={`${!showProfitContent && 'blur hasSeeIcon'}`}
                  onClick={() =>
                    !showProfitContent && handleRecipeRevenueUpgrade()
                  }
                >
                  {showProfitContent
                    ? convertCostCleanly(councilCost)
                    : blurContent(convertCostCleanly(councilCost))}
                </Span>
              ) : (
                <CostLink
                  color="primary"
                  fontSize="small"
                  onClick={() => updateVenueCosts()}
                >
                  Update council cost
                </CostLink>
              )}
            </ListGroupRow>
            <ListGroupRow colOneWidth="180px" colTwoWidth="auto">
              <Label color="grey">Avg Venue Wastage</Label>
              <Span>
                {convertPercentageCleanly(venueData?.avgWastagePercentage || 0)}{' '}
              </Span>
            </ListGroupRow>
            <ListGroupRow colOneWidth="180px" colTwoWidth="auto">
              <Label color="grey">
                {wastageType && capitalizeFirstLetter(wastageType)} Wastage
              </Label>
              <Span>{convertCostCleanly(wastageCost)}</Span>
            </ListGroupRow>
          </ListGroup>
        </div>
      </ListColumns>
    </Container>
  );
};

export default Cost;
